import React from 'react';
import { useTranslation } from 'react-i18next';
import Contact from '../../components/Contact';
import styled from 'styled-components';
import { theme } from '../../theme';

const PageHero = styled.section`
  padding: 140px 2rem 80px;
  background: linear-gradient(180deg, ${theme.colors.bg} 0%, #101722 100%);
  position: relative;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 70% 60% at 50% 0%, rgba(${theme.colors.primaryRgb}, 0.09) 0%, transparent 70%),
      linear-gradient(rgba(148, 163, 184, 0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(148, 163, 184, 0.05) 1px, transparent 1px);
    background-size: auto, 56px 56px, 56px 56px;
    mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.45), transparent 78%);
  }
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 360px);
  gap: 2rem;
  align-items: end;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const Copy = styled.div`
  max-width: 720px;
`;

const Tag = styled.span`
  display: inline-block;
  padding: 0.3rem 0.85rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${theme.colors.primaryLight};
  background: rgba(${theme.colors.primaryRgb}, 0.1);
  border: 1px solid rgba(${theme.colors.primaryRgb}, 0.25);
  margin-bottom: 1.25rem;
`;

const Title = styled.h1`
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  letter-spacing: -0.06em;
  color: ${theme.colors.text};
  margin-bottom: 1rem;

  span {
    background: ${theme.colors.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Sub = styled.p`
  font-size: 1.05rem;
  color: ${theme.colors.textMuted};
  max-width: 520px;
  margin: 0;
  line-height: 1.75;
`;

const LeadCard = styled.aside`
  padding: 1.2rem;
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: linear-gradient(180deg, rgba(17, 25, 34, 0.96), rgba(10, 15, 22, 0.9));

  span {
    display: inline-flex;
    margin-bottom: 0.45rem;
    color: ${theme.colors.primaryLight};
    font-family: 'Space Grotesk', 'Manrope', sans-serif;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  h3 {
    margin-bottom: 0.5rem;
    font-size: 1.3rem;
    font-weight: 700;
    letter-spacing: -0.04em;
    color: ${theme.colors.text};
  }

  p {
    color: ${theme.colors.textMuted};
    font-size: 0.92rem;
    line-height: 1.7;
    margin-bottom: 0.85rem;
  }
`;

const Metrics = styled.div`
  display: grid;
  gap: 0.65rem;
`;

const Metric = styled.div`
  padding: 0.85rem 0.95rem;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.1);
  background: rgba(8, 12, 18, 0.62);

  span {
    display: block;
    margin-bottom: 0.3rem;
    color: ${theme.colors.textMuted};
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  strong {
    color: ${theme.colors.textSecondary};
    font-size: 0.88rem;
    line-height: 1.45;
  }
`;

const ContactPage: React.FC = () => {
  const { t } = useTranslation();

  const metrics = [
    { label: t('contactPage.metric0Label'), value: t('contactPage.metric0Value') },
    { label: t('contactPage.metric1Label'), value: t('contactPage.metric1Value') },
    { label: t('contactPage.metric2Label'), value: t('contactPage.metric2Value') },
  ];

  return (
    <>
      <PageHero>
        <Inner>
          <Copy>
            <Tag>{t('contactPage.tag')}</Tag>
            <Title>
              {t('contactPage.title')} <span>{t('contactPage.titleGradient')}</span>
            </Title>
            <Sub>{t('contactPage.subtitle')}</Sub>
          </Copy>

          <LeadCard>
            <span>{t('contactPage.leadLabel')}</span>
            <h3>{t('contactPage.leadTitle')}</h3>
            <p>{t('contactPage.leadText')}</p>
            <Metrics>
              {metrics.map((metric) => (
                <Metric key={metric.label}>
                  <span>{metric.label}</span>
                  <strong>{metric.value}</strong>
                </Metric>
              ))}
            </Metrics>
          </LeadCard>
        </Inner>
      </PageHero>
      <Contact />
    </>
  );
};

export default ContactPage;
